import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

// ONE REQUEST FOR A LIST MANY READERS WANT AT ONCE.
//
// Every panel that needs this manual's product runs, its sub-step pieces or its listing photos
// asked for them itself, on mounting — and a manual opening mounts them all in the same second.
// The server's log of one open: the product's runs (836 KB) seven times, the sub-step pieces five,
// the listing photos six — some 7 MB of copies, competing with the meshes and pictures that were
// the point.
//
// So a GET through here is shared: whoever asks while one is on its way gets that one, and its
// answer serves for SHARE_MS after it lands — the stagger of a page mounting. Past that, an ask
// goes to the server, as it always did.
//
// AND ANY WRITE FORGETS THEM ALL. Keeping a mesh, adding a photo, deleting a run is followed by a
// read that must see it: the answer from before the write is not one, and a read joined to a
// request already on its way would get it. A write is rare and a list cheap, so every one clears
// every shared answer rather than guessing which list a write touched.
const SHARE_MS = 2000;

const shared = new Map<string, { at: number; got: Promise<AxiosResponse> }>();

// AND AFTER ONE, for a write that answers late: several sent at once clear the answers as they
// leave, and a read made when the first lands is shared with the reads made when the others do,
// which then never see theirs (see listingPhotosSave).
export const forgetShared = () => shared.clear();

axios.interceptors.request.use((config) => {
  if ((config.method ?? 'get').toLowerCase() !== 'get') shared.clear();
  return config;
});

export function sharedGet<T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
  const key = `${url}?${JSON.stringify(config.params ?? {})}`;
  const now = performance.now();
  const was = shared.get(key);
  if (was && now - was.at < SHARE_MS) return was.got as Promise<AxiosResponse<T>>;
  const got = axios.get<T>(url, config);
  const entry = { at: Infinity, got: got as Promise<AxiosResponse> };
  shared.set(key, entry);
  // The window runs from the ANSWER, not the ask: an 836 KB list can take longer than it.
  got.then(() => { entry.at = performance.now(); }, () => { if (shared.get(key) === entry) shared.delete(key); });
  return got;
}
